import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../hooks/useSidebar';
import { Filter, BarChart3 } from 'lucide-react';
import Button from '../../components/ui/Button';


import RiskMatrix from './components/RiskMatrix';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import HazardTrendAnalysis from './components/HazardTrendAnalysis';
import DeviationStatistics from './components/DeviationStatistics';
import ComplianceMetrics from './components/ComplianceMetrics';

const RiskAnalyticsMatrixViewerContent = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'offshore-alpha', label: 'Offshore Platform Alpha' },
    { value: 'offshore-bravo', label: 'Offshore Platform Bravo' },
    { value: 'refinery-01', label: 'Refinery 01' },
    { value: 'terminal-west', label: 'Terminal West' },
  ];

  const timeframes = [
    { value: 'week', label: 'Last Week' },
    { value: 'month', label: 'Last Month' },
    { value: 'quarter', label: 'Last Quarter' },
    { value: 'year', label: 'Last Year' },
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'equipment', label: 'Equipment Hazards' },
    { value: 'process', label: 'Process Hazards' },
    { value: 'chemical', label: 'Chemical Hazards' },
    { value: 'operational', label: 'Operational Hazards' },
  ];

  return (
    <div className={`bg-background min-h-screen`}>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Risk Analytics & Matrix Viewer</h1>
              <p className="text-muted-foreground mt-1">
                Visual risk assessment and compliance analytics
              </p>
            </div>
            <Button
              variant="outline"
              iconName="Home"
              onClick={() => navigate('/')}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Analytics Dashboard */}
          <AnalyticsDashboard
            location={selectedLocation}
            timeframe={selectedTimeframe}
            category={selectedCategory}
          />

          {/* Risk Matrix */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">BP OMS 8×8 Risk Matrix</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Probability vs Consequence Assessment
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </Button>
                <Button size="sm" variant="outline">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Details
                </Button>
              </div>
            </div>
            <RiskMatrix
              location={selectedLocation}
              category={selectedCategory}
            />
          </div>

          {/* Hazard Trend Analysis */}
          <HazardTrendAnalysis
            timeframe={selectedTimeframe}
            location={selectedLocation}
          />

          {/* Bottom Section - Two Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DeviationStatistics
              location={selectedLocation}
              timeframe={selectedTimeframe}
            />
            <ComplianceMetrics
              location={selectedLocation}
              timeframe={selectedTimeframe}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAnalyticsMatrixViewerContent;